import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  deepOrange,
  deepPurple,
  grey,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  red,
  teal,
  yellow,
} from '@mui/material/colors'

declare module '@mui/material/styles' {
  interface Palette {
    neutralLight: {
      main: string
      dark: string
      light: string
      contrastText: string
      0: string
      50: string
      100: string
      300: string
      400: string
      500: string
      700: string
      900: string
    }
    neutralDark: {
      main: string
      dark: string
      light: string
      contrastText: string
      50: string
      75: string
      100: string
      300: string
      500: string
      700: string
      900: string
      999: string
    }
    green: {
      50: string
      100: string
      200: string
      300: string
      500: string
      700: string
      900: string
    }
    red: {
      50: string
      100: string
      200: string
      300: string
      500: string
      700: string
      900: string
    }
    purple: {
      50: string
      100: string
      300: string
      500: string
      700: string
    }
    indigo: {
      50: string
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
    }
    yellow: typeof yellow
    deepOrange: typeof deepOrange
    orange: typeof orange
    lime: typeof lime
    amber: typeof amber
    blue: typeof blue
    lightBlue: typeof lightBlue
    cyan: typeof cyan
    teal: typeof teal
    lightGreen: typeof lightGreen
    brown: typeof brown
    blueGrey: typeof blueGrey
    pink: typeof pink
    deepPurple: typeof deepPurple
    sidebar: {
      text: string
      secondaryText: string
      hoverBackground: string
      selectedBackground: string
      headerBackground: string
      headerBorder: string
      footerBackground: string
      divider: string
      background: string
    }
    header: {
      background: string
      text: string
    }
    riskScore: {
      dark: {
        color: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
        background: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
        border: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
      }
      light: {
        color: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
        border: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
        background: {
          0: string
          1: string
          2: string
          3: string
          4: string
          5: string
        }
      }
    }
    login: {
      tagline: string
      taglineSub: string
    }
    other: {
      stroke: string
    }
    danger: {
      light: string
      main: string
      dark: string
      contrastText: string
      100: string
    }
    border: {
      utility: {
        gold: {
          muted: string
        }
      }
    }
  }

  interface TypeBackground {
    utility: {
      gold: {
        muted: string
      }
    }
    surfaceSubtle: string
  }

  interface PaletteOptions {
    neutralLight: {
      main: string
      dark: string
      light: string
      contrastText: string
      0: string
      50: string
      100: string
      300: string
      400: string
      500: string
      700: string
      900: string
    }
    neutralDark: {
      main: string
      dark: string
      light: string
      contrastText: string
      100: string
      300: string
      700: string
      900: string
      999: string
    }
    green: {
      50: string
      100: string
      200: string
      300: string
      500: string
      700: string
      900: string
    }
    purple: {
      50: string
      100: string
      300: string
      500: string
      700: string
    }
    yellow: typeof yellow
    deepOrange: typeof deepOrange
    orange: typeof orange
    lime: typeof lime
    amber: typeof amber
    blue: typeof blue
    lightBlue: typeof lightBlue
    cyan: typeof cyan
    teal: typeof teal
    lightGreen: typeof lightGreen
    brown: typeof brown
    blueGrey: typeof blueGrey
    pink: typeof pink
    deepPurple: typeof deepPurple
    sidebar: {
      text: string
      secondaryText: string
      hoverBackground: string
      selectedBackground: string
      headerBackground: string
      headerBorder: string
      footerBackground: string
      divider: string
      background: string
    }
    header: {
      background: string
      text: string
    }
    login: {
      tagline: string
      taglineSub: string
    }
    other: {
      stroke: string
    }
    border: {
      utility: {
        gold: {
          muted: string
        }
      }
    }
  }
}

/**
 * @deprecated Use the palette from the theme rather than importing directly
 */
export const palette = {
  text: {
    primary: '#000000',
    secondary: 'rgba(0,0,0,0.54)',
    hint: 'rgba(0,0,0,0.38)',
    warning: {
      default: '#AB6400',
      onWarningMuted: '#4F3422',
    },
  },
  success: {
    main: '#388E3C',
    light: '#E8F5E9',
    contrastText: '#FFF',
    100: '#C8E6C9',
    200: '#E1FEF2',
    500: '#10B981',
  },
  warning: {
    main: '#FF8F00',
    contrastText: '#FFF',
    100: '#FFE082',
    light: '#FFF8E1',
    zero: '#FFF3E0',
    300: '#FFB74D',
    500: '#FF9800',
    900: '#F57F17',
  },
  danger: {
    light: '#FFF3E0',
    main: '#E65100',
    dark: '#E31B0C',
    contrastText: '#FFFFFF',
    100: '#FFA726',
  },
  error: {
    light: '#FEEBEE',
    main: '#E55050',
    dark: '#E31B0C',
    contrastText: '#FFFFFF',
    100: '#FECDD2',
    200: '#F88078',
    500: '#F44336',
  },
  primary: {
    main: '#7B61FF',
    dark: '#3F35CB',
    light: '#F2EDFF',
    contrastText: '#FFF',
    100: '#E1D3FF',
  },
  secondary: {
    main: '#7B61FF',
    dark: '#3F35CB',
    light: '#F2EDFF',
    contrastText: '#FFF',
    100: '#E1D3FF',
  },
  other: {
    stroke: 'rgba(0,0,0, 0.23)',
  },
  green: {
    50: '#E1FEF2',
    100: '#B5F7DB',
    200: '#81C784',
    300: '#5DECB1',
    500: '#10B981',
    700: '#008854',
    800: '#2E7D32',
    900: '#005F3A',
  },
  purple: {
    50: '#F2EDFF',
    100: '#E1D3FF',
    300: '#B290FF',
    500: '#7B61FF',
    700: '#3F35CB',
  },
  indigo: {
    50: '#E8EAF6',
    100: '#C5CAE9',
    200: '#9FA8DA',
    300: '#7986CB',
    400: '#5C6BC0',
    500: '#3F51B5',
    600: '#3949AB',
    700: '#303F9F',
    800: '#283593',
    900: '#1A237E',
  },
  yellow,
  deepOrange,
  red,
  orange,
  lime,
  amber,
  blue,
  lightBlue,
  cyan,
  teal,
  lightGreen,
  brown,
  blueGrey,
  pink,
  deepPurple,
  neutral: {
    main: '#6B6C7E',
    dark: '#393A4A',
    light: '#F5F5F5',
    contrastText: '#FFF',
    100: '#CDCED9',
  },
  neutralLight: {
    main: '#E7E7ED',
    dark: '#CDCED9',
    light: '#F1F2F5',
    contrastText: '#6B6C7E',
    0: '#FFFFFF',
    50: '#F6F7FA',
    100: '#F1F2F5',
    300: '#E7E7ED',
    400: '#E0E0E0',
    500: '#CDCED9',
    700: '#A7A9BC',
    900: '#6B6C7E',
  },
  neutralDark: {
    main: '#20313F',
    dark: '#111116',
    light: '#30313F',
    contrastText: '#FFF',
    50: '#FFFFFF40',
    75: '#FaF9FF1A',
    100: '#393A4A',
    300: '#30313F',
    500: '#20313F',
    700: '#1C1C24',
    900: '#111116',
    999: '#000',
  },
  sidebar: {
    text: '#272833',
    secondaryText: '#6B6C7E',
    icon: '#6B6C7E',
    hoverBackground: '##F6F7FA',
    selectedBackground: '#F1F2F5',
    headerBackground: '#FFF',
    footerBackground: '#FFF',
    headerBorder: '#E7E7ED',
    divider: '#E7E7ED',
    background: '#FFF',
  },
  header: {
    background: '#FFF',
    text: '#212121',
    secondary: '#797C87',
  },
  skeleton: {
    light: '#4D4F5C',
  },
  background: {
    default: '#F6F7FA',
    utility: {
      gold: {
        muted: '#F2F0E7',
      },
    },
    surfaceSubtle: '#F9F9F8',
  },
  riskScore: {
    dark: {
      color: {
        0: '#111116',
        1: '#111116',
        2: '#111116',
        3: '#111116',
        4: '#111116',
        5: '#111116',
      },
      background: {
        0: '#E7E7ED',
        1: 'rgba(205, 220, 57, 0.7)',
        2: 'rgba(251, 206, 45, 0.7)',
        3: 'rgba(255, 167, 38, 0.7)',
        4: 'rgba(245, 124, 0, 0.7)',
        5: 'rgba(239, 83, 80, 0.7)',
      },
      border: {
        0: '#E7E7ED',
        1: 'rgba(205, 220, 57, 0.7)',
        2: 'rgba(251, 206, 45, 0.7)',
        3: 'rgba(255, 167, 38, 0.7)',
        4: 'rgba(245, 124, 0, 0.7)',
        5: 'rgba(239, 83, 80, 0.7)',
      },
    },
    light: {
      color: {
        0: grey[600],
        1: lime[800],
        2: yellow[900],
        3: amber[800],
        4: orange[700],
        5: red[600],
      },
      border: {
        0: grey[400],
        1: lime[600],
        2: yellow[600],
        3: amber[500],
        4: orange[400],
        5: red[400],
      },
      background: {
        0: grey[50],
        1: lime[50],
        2: yellow[50],
        3: amber[50],
        4: orange[50],
        5: red[50],
      },
    },
  },
  login: {
    tagline: 'rgba(239, 241, 244, 0.65)',
    taglineSub: '#EFF1F4',
  },
  border: {
    utility: {
      gold: {
        muted: '#D8D0BF',
      },
    },
  },
}
